/**
 * Copyright 2016-2021 Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

/**
Search provider interface (current qwc2):
-----------------------------------------

  onSearch: function(text, searchParams, callback, axios) {
      // searchParams: {mapcrs, displaycrs, extracrs, lang, limit, filterPoly, filterBBox, cfgParams, ...params}
      callback({results: [ ... ]});   // See below
  }

  getResultGeometry: function(resultItem, callback, axios) {
      callback({geometry: ..., crs: ...});
  }

Providers are enabled per theme by listing their key in the `searchProviders` array of the theme
(see themesConfig.json / CustomApp.jsx). Providers defined here are made known to qwc2 via
`window.QWC2SearchProviders`. The built-in providers `coordinates`, `nominatim`, `qgis` and
`fulltext` are provided by qwc2 itself.

Format of search results:
-------------------------

  results = [
    {
        id: categoryid,            // Unique category ID
        title: display_title,      // Text to display as group title
        type: SearchResultType.PLACE, // Optional, defaults to PLACE (type is now a property of the group)
        items: [
            {
                id: itemid, text: display_text, label: map_label_text,
                x: x, y: y, crs: crs, bbox: [xmin, ymin, xmax, ymax],
                geometry: optional GeoJSON geometry,
                provider: providerid
            }
        ]
    }
  ]
*/

import geojsonBbox from 'geojson-bounding-box';
import CoordinatesUtils from 'qwc2/utils/CoordinatesUtils';
import ConfigUtils from 'qwc2/utils/ConfigUtils';
import Request from 'qwc2/utils/Request';
import {SearchResultType} from 'qwc2/utils/SearchProviders';

import TypeUtils from './utils/TypeUtils';

/** ************************************************************************ **/

function geoAdminLocationSearch(text, searchParams, callback) {
    Request.get("https://api3.geo.admin.ch/rest/services/api/SearchServer?searchText=" + encodeURIComponent(text) + "&type=locations&limit=20")
        .then(response => callback(geoAdminLocationSearchResults(response.data)))
        .catch(() => callback({results: []}));
}

function parseItemBBox(bboxstr) {
    if (bboxstr === undefined) {
        return null;
    }
    const matches = bboxstr.match(/^BOX\s*\(\s*(\d+\.?\d*)\s*(\d+\.?\d*)\s*,\s*(\d+\.?\d*)\s*(\d+\.?\d*)\s*\)$/);
    if (!matches || matches.length < 5) {
        return null;
    }
    const xmin = parseFloat(matches[1]);
    const ymin = parseFloat(matches[2]);
    const xmax = parseFloat(matches[3]);
    const ymax = parseFloat(matches[4]);
    return CoordinatesUtils.reprojectBbox([xmin, ymin, xmax, ymax], "EPSG:21781", "EPSG:4326");
}

function geoAdminLocationSearchResults(obj) {
    const categoryMap = {
        gg25: "Municipalities",
        kantone: "Cantons",
        district: "Districts",
        sn25: "Places",
        zipcode: "Zip Codes",
        address: "Address",
        gazetteer: "General place name directory"
    };
    const resultGroups = {};
    (obj.results || []).forEach(entry => {
        if (resultGroups[entry.attrs.origin] === undefined) {
            resultGroups[entry.attrs.origin] = {
                id: entry.attrs.origin,
                title: categoryMap[entry.attrs.origin] || entry.attrs.origin,
                type: SearchResultType.PLACE,
                items: []
            };
        }
        const x = entry.attrs.lon;
        const y = entry.attrs.lat;
        resultGroups[entry.attrs.origin].items.push({
            id: entry.id,
            text: entry.attrs.label,
            x: x,
            y: y,
            crs: "EPSG:4326",
            bbox: parseItemBBox(entry.attrs.geom_st_box2d) || [x, y, x, y],
            provider: "geoadmin"
        });
    });
    return {results: Object.values(resultGroups)};
}

/** ************************************************************************ **/

function usterSearch(text, searchParams, callback) {
    Request.get("https://webgis.uster.ch/wsgi/search.wsgi?&searchtables=&query=" + encodeURIComponent(text))
        .then(response => callback(usterSearchResults(response.data)))
        .catch(() => callback({results: []}));
}

function usterSearchResults(obj) {
    const results = [];
    let currentgroup = null;
    let groupcounter = 0;
    let counter = 0;
    (obj.results || []).forEach(entry => {
        if (!entry.bbox) {
            // Is group
            currentgroup = {
                id: "ustergroup" + (groupcounter++),
                title: entry.displaytext,
                type: SearchResultType.PLACE,
                items: []
            };
            results.push(currentgroup);
        } else if (currentgroup) {
            currentgroup.items.push({
                id: "usterresult" + (counter++),
                text: entry.displaytext,
                searchtable: entry.searchtable,
                bbox: entry.bbox.slice(0),
                x: 0.5 * (entry.bbox[0] + entry.bbox[2]),
                y: 0.5 * (entry.bbox[1] + entry.bbox[3]),
                crs: "EPSG:21781",
                provider: "uster"
            });
        }
    });
    return {results: results};
}

function usterResultGeometry(resultItem, callback) {
    Request.get("https://webgis.uster.ch/wsgi/getSearchGeom.wsgi?searchtable=" + encodeURIComponent(resultItem.searchtable) + "&displaytext=" + encodeURIComponent(resultItem.text))
        .then(response => callback({geometry: response.data, crs: "EPSG:21781"}))
        .catch(() => callback(null));
}

/** ************************************************************************ **/

function immospectorSearchResults(obj) {
    const results = [];
    const groups = {};
    let groupcounter = 0;

    (obj || []).forEach(entry => {
        const title = TypeUtils.getTypeLabel(entry.objectType.toLowerCase());

        groups[entry.objectType] = {
            id: "immospectorgroup" + (groupcounter++),
            title: title,
            type: SearchResultType.PLACE,
            items: []
        };

        entry.features.forEach(feature => {
            const name = feature.properties.name || feature.properties.description;
            const fid = feature.id || feature.properties.id;

            const featureItem = {
                id: fid,
                text: '' + name,
                label: '' + name,
                provider: "immospector",
                sourceObject: feature,
                // -1: no geometry, selecting the result opens the detail window (handled in overwrite/components/SearchBox)
                type: -1
            };

            if (feature.geometry !== null && typeof(feature.geometry) !== 'undefined') {
                const bounds = geojsonBbox(feature);
                const center = [0.5 * (bounds[0] + bounds[2]), 0.5 * (bounds[1] + bounds[3])];

                featureItem.bbox = bounds;
                featureItem.geometry = feature.geometry;
                featureItem.x = center[0];
                featureItem.y = center[1];
                featureItem.crs = "EPSG:4326";
                featureItem.type = SearchResultType.PLACE;
            }

            groups[entry.objectType].items.push(featureItem);
        });

        results.push(groups[entry.objectType]);
    });
    return {results: results};
}

function immospectorSearch(text, searchParams, callback) {
    const service = ConfigUtils.getConfigProp("immospectorSearchServiceUrl");
    Request.get(service, {params: {
        'searchTerm': text
    }}).then(response => callback(immospectorSearchResults(response.data)))
        .catch(() => callback({results: []}));
}

/** ************************************************************************ **/

function layerSearch(text, searchParams, callback) {
    const results = [];
    if (text === "bahnhof") {
        const layer = {
            sublayers: [
                {
                    name: "a",
                    title: "a",
                    visibility: true,
                    queryable: true,
                    displayField: "maptip",
                    opacity: 255,
                    bbox: {
                        crs: "EPSG:4326",
                        bounds: [
                            8.53289,
                            47.3768,
                            8.54141,
                            47.3803
                        ]
                    }
                }
            ]
        };
        results.push({
            id: "layers",
            title: "Layers",
            type: SearchResultType.THEMELAYER,
            items: [{
                id: "bahnhof",
                text: "Bahnhof",
                layer: layer
            }]
        });
    }
    callback({results: results});
}

/** ************************************************************************ **/

// Providers are enabled via the `searchProviders` list of a theme. `coordinates` and `nominatim`
// are built into qwc2 and therefore not redefined here.
export const SearchProviders = {
    geoadmin: {
        label: "Swisstopo",
        onSearch: geoAdminLocationSearch,
        requiresLayer: "a" // Make provider availability depend on the presence of a theme WMS layer
    },
    uster: {
        label: "Uster",
        onSearch: usterSearch,
        getResultGeometry: usterResultGeometry
    },
    immospector: {
        label: "Immospector",
        onSearch: immospectorSearch,
        handlesGeomFilter: false
    },
    layers: {
        label: "Layers",
        onSearch: layerSearch
    }
};

// Make the providers known to qwc2 (see collectSearchProviders in qwc2/utils/SearchProviders)
window.QWC2SearchProviders = {...(window.QWC2SearchProviders || {}), ...SearchProviders};
